import axios from 'axios';
import apiClient from './apiClient';

interface ApiResponse<T> {
  code: number | string;
  message: string;
  result: T;
}

interface BrandDto {
  id: number;
  name: string;
  createAt: string;
  updateAt: string;
}

interface BrandListDto {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalElements: number;
  data: BrandDto[];
}

export interface Brand {
  id: number;
  name: string;
}

export interface CreateBrandPayload {
  name: string;
}

export type UpdateBrandPayload = CreateBrandPayload;

type BrandAction = 'load' | 'create' | 'update' | 'delete';

class BrandApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BrandApiError';
  }
}

const mapBrand = (brand: BrandDto): Brand => ({
  id: brand.id,
  name: brand.name,
});

const normalizeBrandError = (error: unknown, action: BrandAction): BrandApiError => {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return new BrandApiError('Không thể kết nối đến máy chủ thương hiệu. Vui lòng kiểm tra backend và thử lại.');
    }

    if ((action === 'create' || action === 'update') && error.response.status === 409) {
      return new BrandApiError('Thương hiệu này đã tồn tại.');
    }

    if ((action === 'create' || action === 'update') && error.response.status === 400) {
      return new BrandApiError('Tên thương hiệu không hợp lệ.');
    }

    if ((action === 'update' || action === 'delete') && error.response.status === 404) {
      return new BrandApiError('Thương hiệu không còn tồn tại. Vui lòng tải lại danh sách.');
    }

    if (action === 'delete' && error.response.status === 409) {
      return new BrandApiError('Không thể xóa thương hiệu đang được sử dụng bởi sản phẩm.');
    }
  }

  const fallbackMessages: Record<BrandAction, string> = {
    load: 'Không tải được danh sách thương hiệu. Vui lòng thử lại.',
    create: 'Không tạo được thương hiệu. Vui lòng thử lại.',
    update: 'Không cập nhật được thương hiệu. Vui lòng thử lại.',
    delete: 'Không xóa được thương hiệu. Vui lòng thử lại.',
  };

  return new BrandApiError(fallbackMessages[action]);
};

export const getBrands = async (): Promise<Brand[]> => {
  try {
    const response = await apiClient.get<ApiResponse<BrandListDto>>('/api/brands');
    return response.data.result.data.map(mapBrand);
  } catch (error: unknown) {
    throw normalizeBrandError(error, 'load');
  }
};

export const createBrand = async (payload: CreateBrandPayload): Promise<Brand> => {
  try {
    const response = await apiClient.post<ApiResponse<BrandDto>>('/api/brands', payload);
    return mapBrand(response.data.result);
  } catch (error: unknown) {
    throw normalizeBrandError(error, 'create');
  }
};

export const updateBrand = async (id: number, payload: UpdateBrandPayload): Promise<Brand> => {
  try {
    const response = await apiClient.put<ApiResponse<BrandDto>>(`/api/brands/${id}`, payload);
    return mapBrand(response.data.result);
  } catch (error: unknown) {
    throw normalizeBrandError(error, 'update');
  }
};

export const deleteBrand = async (id: number): Promise<void> => {
  try {
    await apiClient.delete<ApiResponse<void>>(`/api/brands/${id}`);
  } catch (error: unknown) {
    throw normalizeBrandError(error, 'delete');
  }
};
